import { useState } from "react";
import Layout from "../common/Layout";
import { usePageMeta } from "../common/usePageMeta";

const CHOICES = ["Great", "Good", "Could be better", "Disappointed"];

const QUESTIONS = [
  { key: "packaging", label: "Packaging",        desc: "How was the packaging?" },
  { key: "produce",   label: "Produce quality",  desc: "How was the quality of the produce?" },
  { key: "delivery",  label: "Delivery",         desc: "How was the delivery experience?" },
  { key: "value",     label: "Value for money",  desc: "Did you feel you got good value?" },
];

const REORDER_OPTS = [
  { value: "yes",    label: "Yes, definitely" },
  { value: "maybe",  label: "Probably" },
  { value: "no",     label: "Probably not" },
];

function ChoiceGroup({ value, onChange }) {
  return (
    <div className="fb-choice-group">
      {CHOICES.map(c => (
        <button
          key={c}
          type="button"
          className={`fb-choice-btn${value === c ? " fb-choice-btn--on" : ""}`}
          onClick={() => onChange(value === c ? "" : c)}
        >
          {c}
        </button>
      ))}
    </div>
  );
}

export default function FeedbackPage() {
  usePageMeta({ title: 'Feedback | TearDrop Hydro', path: '/feedback', noindex: true });
  const [answers, setAnswers]           = useState({});
  const [wouldReorder, setWouldReorder] = useState("");
  const [comments, setComments]         = useState("");
  const [submitting, setSubmitting]     = useState(false);
  const [submitted, setSubmitted]       = useState(false);
  const [error, setError]               = useState("");

  const set = (key, val) => setAnswers(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async () => {
    setError("");
    const missing = QUESTIONS.filter(q => !answers[q.key]);
    if (missing.length) {
      setError(`Please answer: ${missing.map(q => q.label).join(", ")}`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("https://teardrophydro.co.uk/garageFarm/feedback", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...answers,
          wouldReorder: wouldReorder || null,
          comments:     comments.trim() || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || "Something went wrong");
      }
      setSubmitted(true);
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="fb-page">
          <div className="fb-success">
            <div className="fb-success-icon">✓</div>
            <h2>Thank you!</h2>
            <p>Your feedback has been recorded. It really does help us grow and improve.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="fb-page">

        <header className="fb-header">
          <span className="fb-header-leaf">🌿</span>
          <h1 className="h2" style={{ margin: "0 0 var(--s-2)" }}>Your thoughts</h1>
          <p className="lead" style={{ margin: 0 }}>
            Share your experience - takes about a minute.
          </p>
        </header>

        <div className="fb-card">

          {QUESTIONS.map((q, i) => (
            <div key={q.key}>
              {i > 0 && <hr className="fb-divider" />}
              <div className="fb-section">
                <p className="fb-section-title">{q.desc}</p>
                <ChoiceGroup value={answers[q.key] || ""} onChange={val => set(q.key, val)} />
              </div>
            </div>
          ))}

          <hr className="fb-divider" />

          <div className="fb-section">
            <p className="fb-section-title">Would you order from us again?</p>
            <div className="fb-choice-group">
              {REORDER_OPTS.map(o => (
                <button
                  key={o.value}
                  type="button"
                  className={`fb-choice-btn${wouldReorder === o.value ? " fb-choice-btn--on" : ""}`}
                  onClick={() => setWouldReorder(wouldReorder === o.value ? "" : o.value)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <hr className="fb-divider" />

          <div className="fb-section">
            <p className="fb-section-title">Anything else to add? <span style={{ opacity: .55, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional)</span></p>
            <textarea
              className="fb-textarea"
              placeholder="Flavour notes, suggestions, how you used it…"
              value={comments}
              onChange={e => setComments(e.target.value)}
              maxLength={600}
              rows={3}
            />
            <p className="muted" style={{ fontSize: "var(--fs-12)", margin: 0, textAlign: "right" }}>
              {600 - comments.length} characters remaining
            </p>
          </div>

          {error && (
            <div className="fb-validation" role="alert">
              <span>⚠</span>
              <span>{error}</span>
            </div>
          )}

          <button
            className="btn btn-primary fb-submit"
            onClick={handleSubmit}
            disabled={submitting}
            aria-busy={submitting}
          >
            {submitting ? "Submitting…" : "Submit feedback"}
          </button>

        </div>

        <p className="muted" style={{ textAlign: "center", fontSize: "var(--fs-12)", marginTop: "var(--s-5)" }}>
          Your feedback is anonymous and helps us improve.
        </p>

      </div>
    </Layout>
  );
}
